/**
 * GovOperacao — identidade do agente institucional (nome, tom de voz,
 * horário de atendimento). Reusa `/api/reception/settings`.
 *
 * v0.99.9 · Wave 2 · Fev/2026
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LifebuoyIcon, FloppyDisk } from "@phosphor-icons/react";

export default function GovOperacao() {
  const [cfg, setCfg] = useState({ agent_name: "", agent_display_mode: "brand_agent", company_name: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reception/settings");
        setCfg({
          agent_name: data?.agent_name || "",
          agent_display_mode: data?.agent_display_mode || "brand_agent",
          company_name: data?.company_name || "",
        });
      } catch { /* silent */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/reception/settings", cfg);
      toast.success("Configurações do agente salvas");
    } catch {
      toast.error("Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="gov-operacao-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700/70 font-medium mb-1">Configurações</div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <LifebuoyIcon size={22} className="text-emerald-700" />
          Operação do Agente
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">Identidade e tom de voz do agente institucional no WhatsApp.</p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5 max-w-2xl space-y-4">
        <div>
          <label className="text-[11px] font-medium text-slate-700 mb-1 block">Nome do órgão (aparece no WhatsApp)</label>
          <input
            value={cfg.company_name}
            onChange={(e) => setCfg({ ...cfg, company_name: e.target.value })}
            placeholder="ex.: Prefeitura Municipal de Osasco"
            data-testid="gov-op-company"
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-700 mb-1 block">Nome do agente (opcional)</label>
          <input
            value={cfg.agent_name}
            onChange={(e) => setCfg({ ...cfg, agent_name: e.target.value })}
            placeholder="ex.: Aurora · Atendimento Municipal"
            data-testid="gov-op-agent-name"
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-700 mb-1 block">Modo de apresentação</label>
          <select
            value={cfg.agent_display_mode}
            onChange={(e) => setCfg({ ...cfg, agent_display_mode: e.target.value })}
            data-testid="gov-op-mode"
            className="w-full px-3 py-2 rounded-md border border-slate-200 text-[13px] bg-white focus:border-emerald-600 focus:outline-none"
          >
            <option value="brand_agent">Órgão + Agente (&ldquo;Sou a Aurora da Prefeitura&rdquo;)</option>
            <option value="brand_only">Só o órgão (&ldquo;Prefeitura Municipal responde&hellip;&rdquo;)</option>
            <option value="silent">Silencioso (sem assinatura)</option>
          </select>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="gov-op-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-[13px] font-medium disabled:opacity-60"
        >
          <FloppyDisk size={13} /> {saving ? "Salvando…" : "Salvar identidade"}
        </button>
      </section>
    </div>
  );
}
